import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import TransportManager from "support/transport/TransportManager";
import { setTransport } from "stores/support/supportSlice";

// Owns one TransportManager for the open conversation and turns its normalized
// stream into React state. Views subscribe to the merged messages and the pill
// state; nothing above this hook knows whether a socket or a poll produced them.
export default function useTransport({ uuid, config, sinceId, active }) {
  const dispatch = useDispatch();
  const [live, setLive] = useState([]);
  const [state, setState] = useState("idle");
  const managerRef = useRef(null);

  // sinceId only seeds the watermark. If it were a dependency the transport
  // would tear itself down and reconnect on every message it delivered, which
  // is both a reconnect storm and a guaranteed way to miss the next one.
  const seedRef = useRef(sinceId);
  seedRef.current = sinceId;

  useEffect(() => {
    if (!active || !uuid) {
      return undefined;
    }

    const manager = new TransportManager(config);
    managerRef.current = manager;
    setLive([]);

    const offMessages = manager.on("messages", (incoming) => {
      setLive((current) => {
        const seen = new Set(current.map((m) => m.id));
        const fresh = incoming.filter((m) => m && m.id && !seen.has(m.id));
        return fresh.length ? current.concat(fresh) : current;
      });
    });
    const offState = manager.on("state", (next) => {
      setState(next);
      dispatch(setTransport(next));
    });

    manager.start(uuid, seedRef.current);

    return () => {
      offMessages();
      offState();
      manager.stop();
      managerRef.current = null;
    };
  }, [active, uuid, config, dispatch]);

  return {
    liveMessages: live,
    transportState: state,
    nudge: () => managerRef.current && managerRef.current.nudge(),
  };
}
